import { action, set } from "@ember/object"
import { inject as service } from "@ember/service"
import Component from "@glimmer/component"
import { tracked } from "@glimmer/tracking"
import type { AiMessageDelta, UserMessageData } from "@shared/models/ai"
import type {
  CardAttachment,
  CardStack,
  CardStandard,
  LegacyLessonCardStack,
} from "@shared/models/card-stack"
import type { Course } from "@shared/models/course"
import type { Planbook } from "@shared/models/planbook"
import ObjectId from "@shared/util/object-id-gen"
import { handleError, notifySentry } from "cc-frontend/error-handling"
import prepareLessonAiOpen from "cc-frontend/lib/actions/lesson/LESSON_AI_OPEN"
import prepareLessonAiRespond from "cc-frontend/lib/actions/lesson/LESSON_AI_RESPOND"
import prepareLessonAiSelectSuggestion from "cc-frontend/lib/actions/lesson/LESSON_AI_SELECT_SUGGESTION"
import prepareLessonAiSend from "cc-frontend/lib/actions/lesson/LESSON_AI_SEND"
import type { CardSetStandardIdsCommand } from "cc-frontend/lib/ai/ai-stream-parser"
import { AiStreamParser } from "cc-frontend/lib/ai/ai-stream-parser"
import type { AiValueEditor } from "cc-frontend/lib/ai/ai-value-editor"
import type AiChats from "cc-frontend/services/ai-chats"
import type Dialog from "cc-frontend/services/dialog"
import type Dispatcher from "cc-frontend/services/dispatcher"
import type EmberStore from "cc-frontend/services/ember-store"
import type Filestack from "cc-frontend/services/filestack"
import type LessonEditor from "cc-frontend/services/lesson-editor"
import type { ModelsNeededForFirstEdit } from "cc-frontend/services/lesson-editor"
import type StandardsModalService from "cc-frontend/services/standards-modal"
import fade from "ember-animated/transitions/fade"
import type { FocusTrap } from "focus-trap"
import { createFocusTrap } from "focus-trap"
import { cloneDeep } from "lodash"
import { Converter } from "showdown"

import styles from "./ai.module.css"

/**
 * How long to wait from when we send a message until we receive the first message delta.
 * This may need to increase if we enable thinking.
 */
const START_TIMEOUT = 30_000

/**
 * How long to wait between message deltas before timing out.
 */
const DELTA_TIMEOUT = 15_000

enum AiState {
  // User's turn to send a message.
  USER_ASK = "USER_ASK",
  // Same but it's the first message.
  USER_ASK_FIRST = "USER_ASK_FIRST",
  // User needs to acccept or reject edits.
  USER_ACCEPT_REJECT = "USER_ACCEPT_REJECT",
  // Assistant is responding (either thinking or streaming).
  ASSISTANT_RESPONDING = "ASSISTANT_RESPONDING",
  // The user has hit a rate limit. Currently, this can only happen when joining
  // a chat, so we should learn about it before the user types anything.
  RATE_LIMITED = "RATE_LIMITED",
  // A non-retryable error occurred while the assistant was responding.
  ERROR_PERMANENT = "ERROR_PERMANENT",
  // A retryable error occurred while the assistant was responding.
  ERROR_RETRYABLE = "ERROR_RETRYABLE",
}

/**
 * A message in the conversation history, in the form used for displaying here
 * (instead of an AiMessage).
 *
 * Includes in-progress assistant messages.
 */
type HistoryMessage = HistoryMessageUser | HistoryMessageAssistant

interface HistoryMessageUser {
  role: "user"
  indexInChat: number
  text: string
  files: CardAttachment[]
  standards: CardStandard[]
}

interface HistoryMessageAssistant {
  role: "assistant"
  html: string | null
}

interface Suggestion {
  id: string
  title: string
  prompt: string
}

interface Signature {
  Args: {
    lesson: CardStack
    perspective: "day" | "week" | "month"
    modelsNeededForFirstEdit: ModelsNeededForFirstEdit
    course: Course
    planbook: Planbook
    dateString: string
    /**
     * AiValueEditor for each relevant card, keyed by `${card.id}:${prop}`,
     * as well as the lesson title's editor keyed by "title".
     */
    aiValueEditors: Map<string, AiValueEditor<unknown>>
    onClose: () => void
  }
}

const mdConverter = new Converter({
  strikethrough: true,
  // TODO: What are these converted to?
  tasklists: true,
  smoothLivePreview: true,
  disableForced4SpacesIndentedSublists: true,
  simpleLineBreaks: true,
  backslashEscapesHTMLTags: true,
})

export default class CardStacksAi extends Component<Signature> {
  readonly styles = styles
  fade = fade
  @service declare aiChats: AiChats
  @service declare filestack: Filestack
  @service declare dialog: Dialog
  @service declare lessonEditor: LessonEditor
  @service declare standardsModal: StandardsModalService
  @service("ember-store") declare store: EmberStore
  @service declare dispatcher: Dispatcher

  readonly chatId: string

  private focusTrap!: FocusTrap
  private isDeactivatingFocusTrap = false
  private isUnloading = false

  // Interaction state
  @tracked currentState: AiState = AiState.USER_ASK_FIRST
  @tracked showAttachMenu = false
  @tracked showSuggestionsMenu = false
  @tracked isShowingFilePicker = false
  @tracked isInExitDialog = false
  @tracked isClosing = false
  @tracked hasPendingEdits = false
  @tracked rateLimitMessage: string | null = null

  // Pending user message state
  @tracked inputValue = ""
  @tracked pendingFiles: CardAttachment[] = []
  @tracked pendingStandards: CardStandard[] = []
  private nextIndexInChat = 0

  // Pending (incomplete) assistant message state
  // State machine for converting streaming deltas to UI updates.
  pendingAssistantParser: AiStreamParser | null = null
  // Whether we have any text from the assistant yet.
  // Note: The text itself is stored directly in the history.
  @tracked hasAssistantText = false
  nextTimeout: ReturnType<typeof setTimeout> | null = null
  // The lesson at the start of the editing session (not necessarily this message, if the user asked to revise).
  originalLesson: LegacyLessonCardStack | null = null
  // Which lesson/card props have changed for the current editing session.
  changedCardProps = new Map<string, Set<string>>()
  isTitleChanged = false
  // The AI's summary of its own changes to each card for the current message.
  @tracked cardChangeSummaries: Array<{ cardId: string; cardTitle: string; changes: string }> = []

  // Conversation history. Set the variable directly (instead of mutating a TrackedArray in-place)
  // to work around a compat issue with AnimatedEach.
  @tracked history: Array<HistoryMessage> = []

  readonly suggestions: Array<Suggestion> = [
    {
      id: "DIFFERENTIATE",
      title: "Differentiate",
      prompt:
        "Help me adjust this lesson to support all kinds of learners — including students with IEPs, English learners, and kids who need more challenge or more support.",
    },
    {
      id: "IMPROVE",
      title: "Improve",
      prompt:
        "Take a look at this lesson and help me make it clearer, stronger, and better aligned to the standards.",
    },
    {
      id: "IDEAS",
      title: "Give me Ideas",
      prompt:
        "I'm looking for fresh ideas to teach the main concepts in this lesson. What creative or effective approaches could I try?",
    },
    {
      id: "QUESTIONS",
      title: "Add questions",
      prompt:
        "Add some thoughtful questions I can use to check for understanding — a mix of comprehension and higher-order thinking would be great.",
    },
    {
      id: "HOMEWORK",
      title: "Add homework",
      prompt:
        "Suggest a few meaningful homework options that reinforce what students learned, without overwhelming them.",
    },
    {
      id: "ENGAGE",
      title: "Engage",
      prompt:
        "Help me make this lesson more fun and interactive — maybe with a game, a hands-on activity, or some student choice.",
    },
    {
      id: "GRAMMAR",
      title: "Fix grammar",
      prompt:
        "Check this lesson for grammar, punctuation, and spelling mistakes, and fix them without changing the meaning.",
    },
    {
      id: "REFORMAT",
      title: "Reformat",
      prompt:
        "Clean up the formatting so it’s easier to read and follow — something that’s clear for both teachers and students.",
    },
    {
      id: "SIMPLIFY",
      title: "Simplify",
      prompt:
        "Rewrite this using simpler words and shorter sentences, so it’s easier for younger kids or early readers to understand.",
    },
    {
      id: "EXAMPLES_REAL_WORLD",
      title: "Add real-world examples",
      prompt:
        "Add examples that connect this lesson to real life — things students might see, use, or care about.",
    },
    {
      id: "ASSESSMENT",
      title: "Assessment",
      prompt:
        "Suggest both quick check-ins and end-of-lesson assessments to help me see how well students understood the material.",
    },
  ]

  // ----------
  // SETUP / TEARDOWN
  // ----------

  constructor(owner: unknown, args: Signature["Args"]) {
    super(owner, args)

    this.chatId = ObjectId.create()
    this.aiChats.create(
      this.chatId,
      this.args.lesson.id,
      {
        courseId: this.args.course.id,
        planbookId: this.args.planbook.id,
        dateString: this.args.dateString,
        isUnified: false,
      },
      this.onMessageDeltas,
      this.onMessageCompleted,
      this.onRateLimited,
      this.onError
    )

    // Preload these so we can update standards faster in onMessageDeltas.
    for (const standardSetId of this.args.course.attributes.standardSetIds) {
      void this.store.find("standard-set", standardSetId)
    }

    window.addEventListener("beforeunload", this.guardBeforeUnload)

    this.dispatcher.dispatchPreparedAction(
      prepareLessonAiOpen({
        chatId: this.chatId,
        cardStackId: this.args.lesson.id,
        lessonTitle: this.args.lesson.attributes.title || "",
        planbookId: this.args.planbook.id,
      })
    )
  }

  willDestroy() {
    super.willDestroy()
    if (this.chatId && this.aiChats) {
      this.aiChats.close(this.chatId)
    }
    window.removeEventListener("beforeunload", this.guardBeforeUnload)
    this.deactivateFocusTrap()
  }

  private guardBeforeUnload = (e: BeforeUnloadEvent) => {
    if (this.hasPendingEdits && !(this.isDestroying || this.isDestroyed)) {
      e.preventDefault()
      this.tryExit()
    } else {
      this.isUnloading = true
    }
  }

  doClose() {
    // Set this to deactivate the focus trap, so that it won't be triggered while we're animating out.
    this.isClosing = true

    this.args.onClose()
  }

  // ----------
  // FOCUS TRAP
  // ----------

  @action
  initFocusTrap(container: HTMLDivElement) {
    this.focusTrap = createFocusTrap(container, {
      preventScroll: true,
      escapeDeactivates: true,
      clickOutsideDeactivates: true,
      onDeactivate: () => {
        if (!this.isDeactivatingFocusTrap) {
          // Try to exit the AI chat when the *user* deactivates the focus trap,
          // first prompting for confirmation if needed.
          // The trap will be re-activated afterwards if they decide not to exit.
          this.tryExit()
        }
      },
    })
    this.updateFocusTrap()
  }

  get shouldTrapFocus() {
    // We need to not trap focus when the attachment menu or a modal is open,
    // since those are outside of our DOM tree.
    return (
      this.hasPendingEdits &&
      !(
        this.showAttachMenu ||
        this.standardsModal.isOpen ||
        this.showSuggestionsMenu ||
        this.isShowingFilePicker ||
        this.isInExitDialog ||
        this.isClosing
      )
    )
  }

  @action
  updateFocusTrap() {
    if (this.shouldTrapFocus) this.focusTrap.activate()
    else this.deactivateFocusTrap()
  }

  private deactivateFocusTrap() {
    // Don't trigger tryExit when we call deactivate ourselves.
    this.isDeactivatingFocusTrap = true
    try {
      this.focusTrap.deactivate()
    } finally {
      this.isDeactivatingFocusTrap = false
    }
  }

  // ----------
  // MESSAGE INPUT
  // ----------

  get showInput() {
    return this.currentState === AiState.USER_ASK || this.currentState === AiState.USER_ASK_FIRST
  }

  get isSendDisabled() {
    return !this.inputValue.trim()
  }

  get placeholderText() {
    if (this.hasPendingEdits) {
      return "Ask AI to revise"
    } else return "Ask AI anything"
  }

  @action
  updateInputValue(value) {
    // Ignore stray updates during the fade-out animation.
    if (!this.showInput) return

    this.inputValue = value
  }

  @action
  handleKeydown(event) {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault()
      this.handleSend()
    }
  }

  @action
  handleSend() {
    if (this.isSendDisabled) return

    const text = this.inputValue.trim()
    if (!text) return

    this.sendMessage(this.nextIndexInChat, text, this.pendingFiles, this.pendingStandards)
  }

  private sendMessage(
    indexInChat: number,
    text: string,
    files: CardAttachment[],
    standards: CardStandard[]
  ): void {
    // Convert to UserMessageData for sending.
    const messageData: UserMessageData = {
      indexInChat,
      text,
      files: files.map((file) => ({
        title: file.title,
        url: file.url,
        mimetype: file.mimetype,
      })),
      standards: standards.map((standard) => ({
        id: standard.id,
        code: standard.code,
        statement: standard.statement,
      })),
    }

    // Remove the previous user and previous AI message.
    // We do this for all messages (not just retries) so that the chat history only shows
    // the most recent messages.
    // Store message in the conversation history.
    this.history = [
      ...this.history.slice(2),
      {
        role: "user",
        indexInChat,
        text,
        files,
        standards,
      },
      // Also create an entry for the coming assistant text.
      // It won't be rendered until it has nontrivial content.
      {
        role: "assistant",
        html: null,
      },
    ]

    // Update interaction state and reset the pending input.
    this.currentState = AiState.ASSISTANT_RESPONDING

    this.isShowingFilePicker = false
    this.showSuggestionsMenu = false

    this.inputValue = ""
    this.pendingFiles = []
    this.pendingStandards = []
    this.nextIndexInChat = indexInChat + 1

    if (this.originalLesson === null) {
      this.originalLesson = cloneDeep(this.args.lesson as LegacyLessonCardStack)
    }
    this.pendingAssistantParser = new AiStreamParser(this.originalLesson!)
    this.hasAssistantText = false
    this.cardChangeSummaries = []

    // Tell editors that AI edits are starting, unless they're still waiting for
    // the previous edits to complete (see onMessageCompleted).
    // Note: If the AI starts editing but fails and we retry, we don't reset the pending edits
    // to the state before the AI started editing. I think that's okay as the AI will likely edit
    // the same cards on retry, hence overwrite all of the failed edits.
    if (!this.hasPendingEdits) this.startAiEdits()

    this.aiChats.sendMessage(this.chatId, messageData)
    this.nextTimeout = setTimeout(() => this.onError("Start timeout", true), START_TIMEOUT)

    this.dispatcher.dispatchPreparedAction(
      prepareLessonAiSend({
        indexInChat,
        chatId: this.chatId,
        cardStackId: this.args.lesson.id,
        lessonTitle: this.args.lesson.attributes.title || "",
        planbookId: this.args.planbook.id,
      })
    )
  }

  // ----------
  // SUGGESTIONS
  // ----------

  @action
  handleSuggestionClick(suggestion: Suggestion) {
    this.inputValue = this.inputValue + "\n" + suggestion.prompt
    this.showSuggestionsMenu = false

    this.dispatcher.dispatchPreparedAction(
      prepareLessonAiSelectSuggestion({
        suggestionId: suggestion.id,
        indexInChat: Math.floor(this.history.length / 2),
        chatId: this.chatId,
        cardStackId: this.args.lesson.id,
        lessonTitle: this.args.lesson.attributes.title || "",
        planbookId: this.args.planbook.id,
      })
    )
  }

  // ----------
  // ATTACHMENT MENU
  // ----------

  @action
  handleAttach() {
    this.showAttachMenu = !this.showAttachMenu
  }

  @action
  closeAttachMenu() {
    this.showAttachMenu = false
  }

  @action
  async handleAddStandard() {
    this.showAttachMenu = false
    const { standards } = await this.standardsModal.openStandardsModal(
      this.pendingStandards,
      this.args.planbook,
      this.args.course
    )
    this.pendingStandards = standards.slice()
  }

  @action
  removeSelectedStandard(standard: CardStandard) {
    this.pendingStandards = this.pendingStandards.filter((s) => s.id !== standard.id)
  }

  @action
  handleUploadFile() {
    this.showAttachMenu = false
    this.isShowingFilePicker = true

    const picker = this.filestack.client.picker({
      // Mime types accepted by Gemini. The list is missing from Gemini's docs, but it's
      // probably the same as for Vertex: https://cloud.google.com/vertex-ai/generative-ai/docs/model-reference/inference#blob
      accept: [
        "application/pdf",
        "audio/mpeg",
        "audio/mp3",
        "audio/wav",
        "image/png",
        "image/jpeg",
        "image/webp",
        "text/plain",
        "video/mov",
        "video/mpeg",
        "video/mp4",
        "video/mpg",
        "video/avi",
        "video/wmv",
        "video/mpegps",
        "video/flv",
      ],
      fromSources: ["local_file_system"],
      maxFiles: 20,
      maxSize: 10 * 1024 * 1024,
      onUploadDone: (res) => {
        let files = this.filestack.asAttachments(res.filesUploaded)
        const cardAttachments: CardAttachment[] = files.map((file) => ({
          id: ObjectId.create(),
          title: file.title,
          url: file.url,
          type: file.type,
          mimetype: file.mimetype,
          size: file.size,
          isWriteable: file.isWriteable || false,
        }))
        this.pendingFiles = [...this.pendingFiles, ...cardAttachments]
      },
      onFileUploadFailed: (error) => {
        console.error("File upload failed:", error)
      },
      onClose: () => {
        this.isShowingFilePicker = false
      },
    })
    picker.open()
  }

  @action
  removeAttachedFile(fileIndex) {
    this.pendingFiles = this.pendingFiles.filter((_, index) => index !== fileIndex)
  }

  // ----------
  // ACCEPT/REJECT/REVISE ACTIONS
  // ----------

  private getCardEditor<T>(cardId: string, prop: string): AiValueEditor<T> | undefined {
    const editor = this.args.aiValueEditors.get(`${cardId}:${prop}`)
    if (!editor) {
      console.error(`Card editor not found for ${cardId}:${prop}`)
      return undefined
    }
    return editor as AiValueEditor<T>
  }

  @action
  startAiEdits() {
    for (const editor of this.args.aiValueEditors.values()) {
      editor.startAiEdits()
    }
  }

  @action
  handleAccept() {
    this.recordResponse("accept")

    // Mark all editors as accepted, storing the AI values.
    // Note: We need to do this even for editors that received no AI edits,
    // so that they can update their interaction states.
    const aiValues = new Map<string, unknown>()
    for (const [key, editor] of this.args.aiValueEditors) {
      const newValue = editor.acceptAiEdits()
      aiValues.set(key, newValue)
    }

    // Apply the accepted edits.
    const cardChanges = new Map<
      string,
      Array<{
        prop: string
        originalValue: unknown
        value: unknown
      }>
    >()
    for (const [cardId, changedProps] of this.changedCardProps) {
      const originalCard = this.pendingAssistantParser!.findOriginalCard(cardId)!

      const propChanges: Array<{
        prop: string
        originalValue: unknown
        value: unknown
      }> = []
      for (const prop of changedProps) {
        const key = `${cardId}:${prop}`
        if (!aiValues.has(key)) {
          console.error(`No editor value found for ${key}, skipping update`)
          continue
        }
        const value = aiValues.get(key)!
        const originalValue = originalCard.attributes[prop]
        propChanges.push({ prop, originalValue, value })
      }

      cardChanges.set(cardId, propChanges)
    }
    this.lessonEditor.acceptAiChanges(
      this.args.modelsNeededForFirstEdit,
      this.args.lesson,
      cardChanges,
      this.isTitleChanged ? (aiValues.get("title") as string | undefined) : undefined,
      this.originalLesson!.attributes.title ?? null,
      this.chatId,
      this.args.planbook.id
    )

    // Close the AI display.
    this.doClose()
  }

  @action
  handleReject() {
    this.recordResponse("reject")

    // Reject the edits.
    for (const editor of this.args.aiValueEditors.values()) {
      editor.rejectAiEdits()
    }

    // Close the AI display.
    this.doClose()
  }

  @action
  handleRevise() {
    this.recordResponse("revise")

    this.currentState = AiState.USER_ASK

    // Leave the AiValueEditors in their active state, so that they can show
    // a diff from the original lesson to the new revisions.
  }

  @action
  handleTryAgain() {
    // Try again. Note that we overwrite the previous response, not keep it.
    const prevUserMessage = this.history[this.history.length - 2] as HistoryMessageUser
    this.sendMessage(
      prevUserMessage.indexInChat,
      prevUserMessage.text,
      prevUserMessage.files,
      prevUserMessage.standards
    )
  }

  private recordResponse(response: "accept" | "reject" | "revise") {
    this.dispatcher.dispatchPreparedAction(
      prepareLessonAiRespond({
        response,
        indexInChat: Math.ceil(this.history.length / 2) - 1,
        chatId: this.chatId,
        cardStackId: this.args.lesson.id,
        lessonTitle: this.args.lesson.attributes.title || "",
        planbookId: this.args.planbook.id,
      })
    )
  }

  @action
  tryExit() {
    if (this.hasPendingEdits) {
      this.isInExitDialog = true
      this.dialog
        .blank("dialogs/confirm-exit-ai", {
          className: "dialog--create-annotation",
        })
        .then((editResult: "accept" | "reject") => {
          if (editResult === "accept") {
            this.handleAccept()
          } else {
            this.handleReject()
          }
        })
        .catch((error) => {
          if (error instanceof Error) handleError(error)
          // Else user hit Cancel in the dialog.
        })
        .finally(() => {
          // This will reactivate the focus trap (via our did-update).
          this.isInExitDialog = false
        })
    } else {
      this.doClose()
    }
  }

  // ----------
  // AI CHAT SERVICE CALLBACKS
  // ----------

  private onMessageDeltas = (deltas: AiMessageDelta[]) => {
    if (
      this.currentState === AiState.ERROR_PERMANENT ||
      this.currentState === AiState.ERROR_RETRYABLE
    )
      return

    if (this.nextTimeout) clearTimeout(this.nextTimeout)
    this.nextTimeout = setTimeout(() => this.onError("Delta timeout", true), DELTA_TIMEOUT)

    const textDelta = deltas
      .filter((delta) => delta.content && delta.content.type === "text")
      .map((delta) => delta.content!.content)
      .join("")

    const commands = this.pendingAssistantParser!.applyDelta(textDelta)
    for (const command of commands) {
      switch (command.type) {
        case "chatAppend":
          if (command.text !== "") {
            this.hasAssistantText = true
            const historyMessage = this.history[this.history.length - 1] as HistoryMessageAssistant
            set(historyMessage, "html", mdConverter.makeHtml(command.text))
          }
          break
        case "setTitle": {
          const editor = this.args.aiValueEditors.get("title") as AiValueEditor<string> | undefined
          if (editor) {
            editor.setValueFromAi(command.title, true)
          } else {
            console.error("Lesson title editor not found")
          }
          this.isTitleChanged = true
          break
        }
        case "cardAppendValue":
          if (command.cardType === "card-google-classroom") {
            this.getCardEditor<string>(command.id, "value")?.setValueFromAi(
              command.value,
              command.isDone
            )
          } else {
            // TODO: Move this conversion to ScribeEditor.
            const valueHtml = mdConverter.makeHtml(command.value)
            this.getCardEditor<string>(command.id, "value")?.setValueFromAi(
              valueHtml,
              command.isDone
            )
          }
          this.addChangedCardProp(command.id, "value")
          break
        case "cardSetStandardIds":
          this.cardSetStandardIds(command)
          break
        case "cardIssueChanges":
          this.cardChangeSummaries = [
            ...this.cardChangeSummaries,
            { cardId: command.id, cardTitle: command.title, changes: command.changes },
          ]
          break
      }

      if (command.type !== "chatAppend") this.hasPendingEdits = true
    }
  }

  private onMessageCompleted = () => {
    if (
      this.currentState === AiState.ERROR_PERMANENT ||
      this.currentState === AiState.ERROR_RETRYABLE
    )
      return

    if (this.nextTimeout) clearTimeout(this.nextTimeout)

    console.log("AI response completed")

    this.currentState = this.hasPendingEdits ? AiState.USER_ACCEPT_REJECT : AiState.USER_ASK

    if (!this.hasPendingEdits) {
      // Nothing was edited; restore the editors to their normal state.
      // They'll go back into AI mode on the next message send.
      for (const editor of this.args.aiValueEditors.values()) {
        editor.rejectAiEdits()
      }
    }
  }

  private onRateLimited = (message: string) => {
    this.rateLimitMessage = message
    this.currentState = AiState.RATE_LIMITED
  }

  private onError = (error: unknown, isRetryable: boolean) => {
    // We don't care unless the AI is currently responding.
    if (this.currentState !== AiState.ASSISTANT_RESPONDING) return
    // Don't show the error text or notify Sentry if the user is just refreshing the page.
    if (this.isUnloading) return

    if (this.nextTimeout) clearTimeout(this.nextTimeout)

    // Log the error.
    // We can filter these out as we learn which ones are normal (e.g. client network errors).
    const id = ObjectId.create()
    console.error("AI chat error", error, id)
    if (error instanceof Error) notifySentry(error, id, false)
    else notifySentry("Ai chat error: " + error, id, false)

    this.currentState =
      isRetryable && this.history.length > 0 ? AiState.ERROR_RETRYABLE : AiState.ERROR_PERMANENT

    if (!this.hasPendingEdits) {
      // Nothing was edited; restore the editors to their normal state.
      // They'll go back into AI mode on the next message send.
      for (const editor of this.args.aiValueEditors.values()) {
        editor.rejectAiEdits()
      }
    }

    // Keep the current incomplete assistant state, including edits.
    // The user can accept or reject them by exiting.
  }

  // ----------
  // CARD EDITING COMMANDS
  // ----------

  private cardSetStandardIds(command: CardSetStandardIdsCommand) {
    // Convert standardIds to standards as in csp/search-model.js#toggleStandard.
    // It's okay that this is async.
    const standardSetPromises = this.args.course.attributes.standardSetIds.map((id) =>
      this.store.find("standard-set", id)
    )
    Promise.all(standardSetPromises).then((standardSets) => {
      const standards = command.standardIds
        .map((standardId) => {
          for (const standardSet of standardSets) {
            const standard = standardSet.attributes.standards[standardId]
            if (standard) {
              // @ts-expect-error CardStandard type doesn't reflect what's actually stored
              return {
                id: standard.id,
                code: standard["altStatementNotation"] || standard.statementNotation,
                grades: standardSet.attributes.title,
                subject: standardSet.attributes.subject,
                standardSetId: standardSet.id,
                jurisdiction: standardSet.attributes.jurisdiction.title,
                statement: standard.description,
                clarifications: standard["comments"],
              } as CardStandard
            }
          }
          console.error(`AI added standardId ${standardId} but it was not found.`)
          return null
        })
        .filter((standard) => standard !== null)

      if (![AiState.ASSISTANT_RESPONDING, AiState.USER_ACCEPT_REJECT].includes(this.currentState)) {
        const error = "Standards took too long to resolve, skipping AI standards edit"
        console.error(error)
        notifySentry(error, ObjectId.create(), false)
        return
      }

      this.getCardEditor<CardStandard[]>(command.id, "standards")?.setValueFromAi(standards, true)
      this.addChangedCardProp(command.id, "standards")
    })
  }

  private addChangedCardProp(cardId: string, prop: string) {
    let byCard = this.changedCardProps.get(cardId)
    if (!byCard) {
      byCard = new Set()
      this.changedCardProps.set(cardId, byCard)
    }
    byCard.add(prop)
  }
}

declare module "@glint/environment-ember-loose/registry" {
  export default interface Registry {
    "Planner::CardStacks::Ai": typeof CardStacksAi
  }
}
